// Link-share (Option A) PDF view + consent-gated download.
//
// Two variants, dispatched on `metadata.encrypted`:
//
// PLAINTEXT (`encrypted` false/absent) — unchanged legacy behavior:
// 1. Render header + inline PDF view (browser-native iframe with
//    application/pdf MIME from the backend).
// 2. Render a consent checkbox + disabled Download button. Button
//    enables only when the checkbox is checked.
// 3. On Download click: POST `/share/links/{token}/ack` with
//    {consent_ack: true}; then trigger browser save via a temporary
//    `<a download>` link to the same `/share/links/{token}` URL.
// 4. The audit chain on the patient's vault sees ShareLinkOpened
//    with recipient_consent_ack=true.
//
// ENCRYPTED (`encrypted` true) — zero-custody anyone-share:
// 1. Extract the CEK from the URL fragment (`#k=<base64url>`); if
//    absent, render the "missing share key" error card.
// 2. Same consent gate. On Download click: POST the ack, then FETCH
//    the link download URL as bytes (the body is an opaque
//    ChaCha20-Poly1305 ciphertext, not a PDF — so no inline preview).
// 3. Decrypt client-side with the CEK (WebCrypto); on failure, render
//    the decrypt-failure error card.
// 4. Trigger a browser save of the decrypted PDF bytes.

import type { DiscoverResponse } from "./discover.js";
import { decryptBundle, DecryptError, extractCEKFromHash } from "./crypto.js";

export interface PdfViewProps {
  apiBaseUrl: string;
  token: string;
  metadata: DiscoverResponse;
}

export function renderPdfView(root: HTMLElement, props: PdfViewProps): void {
  const encrypted = props.metadata.encrypted === true;

  // For an encrypted link the CEK must ride in the URL fragment. If it's
  // missing we can't decrypt anything, so bail before rendering the view
  // (mirrors share-session.ts).
  let cek: Uint8Array | null = null;
  if (encrypted) {
    cek = extractCEKFromHash(window.location.hash);
    if (!cek) {
      renderError(
        root,
        "Missing share key",
        "This URL doesn't include the encryption key. The link may have been edited or copied incorrectly.",
      );
      return;
    }
  }

  const expiresAt = new Date(props.metadata.expires_at);
  const expiresHuman = expiresAt.toLocaleString();
  const sizeKb = props.metadata.byte_length
    ? Math.ceil(props.metadata.byte_length / 1024)
    : null;

  // The encrypted body is opaque ciphertext, so an inline iframe preview
  // would render garbage — only show the native PDF preview for the
  // plaintext variant.
  const previewHtml = encrypted
    ? ""
    : `
      <iframe
        class="pdf-frame"
        src="${props.apiBaseUrl}/share/links/${encodeURIComponent(props.token)}#toolbar=0&navpanes=0"
        title="Shared health summary"
        sandbox="allow-scripts allow-same-origin allow-downloads">
      </iframe>`;

  root.innerHTML = `
    <div class="card">
      <h1>Patient-shared health summary</h1>
      <p>
        A patient has shared a health summary with you via BlockHaven.
        ${sizeKb ? `Approximate size: ${sizeKb} KB.` : ""}
        Expires ${expiresHuman}.
      </p>
      ${previewHtml}
      <label class="consent">
        <input type="checkbox" id="consent-ack" />
        <span>
          I acknowledge I am authorized to receive this summary.
          The patient will see in their audit log that this share was
          opened and downloaded.
        </span>
      </label>
      <div class="actions">
        <button id="download-btn" class="primary" disabled>
          Download PDF
        </button>
      </div>
    </div>
  `;

  const checkbox = root.querySelector<HTMLInputElement>("#consent-ack");
  const button = root.querySelector<HTMLButtonElement>("#download-btn");
  if (!checkbox || !button) {
    return;
  }

  checkbox.addEventListener("change", () => {
    button.disabled = !checkbox.checked;
  });

  button.addEventListener("click", async () => {
    button.disabled = true;
    button.textContent = "Recording consent…";
    try {
      // Record the consent ack first. If this fails we still let
      // the download proceed (the download itself emits
      // ShareLinkOpened with consent_ack=false which the patient
      // will see — that's the right audit semantic).
      await postAck(props.apiBaseUrl, props.token, true);
    } catch (e) {
      console.warn("ack post failed:", e);
    }

    if (encrypted) {
      // cek is non-null here — verified above before rendering the view.
      const ok = await downloadDecryptAndSave(
        root,
        props,
        cek as Uint8Array,
        button,
      );
      if (ok) {
        button.textContent = "Downloaded";
      }
      // On failure downloadDecryptAndSave has replaced the view with an
      // error card, so the button no longer exists in the DOM.
      return;
    }

    triggerPlaintextDownload(props.apiBaseUrl, props.token);
    button.textContent = "Downloaded";
  });
}

/**
 * Encrypted variant: fetch the opaque ciphertext from the link download
 * URL, decrypt it with the CEK, and save the resulting PDF. Returns true
 * on success; on failure renders an inline error card and returns false.
 */
async function downloadDecryptAndSave(
  root: HTMLElement,
  props: PdfViewProps,
  cek: Uint8Array,
  button: HTMLButtonElement,
): Promise<boolean> {
  button.textContent = "Downloading…";

  let sealed: Uint8Array;
  try {
    const url = `${props.apiBaseUrl}/share/links/${encodeURIComponent(props.token)}`;
    const resp = await fetch(url, {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
    });
    // A view-once link consumed between discover and this fetch (a concurrent
    // open) returns 410 {"error":"consumed"} — surface the accurate terminal
    // state rather than a generic download failure.
    if (resp.status === 410) {
      const b = (await resp.json().catch(() => ({}))) as { error?: string };
      renderError(
        root,
        b.error === "consumed" ? "Already viewed" : "No longer available",
        b.error === "consumed"
          ? "This emergency card link has already been viewed and is no longer available."
          : "This share link has been revoked or expired.",
      );
      return false;
    }
    if (!resp.ok) {
      throw new Error(`Download failed: HTTP ${resp.status}`);
    }
    sealed = new Uint8Array(await resp.arrayBuffer());
  } catch (e) {
    renderError(
      root,
      "Couldn't download summary",
      e instanceof Error ? e.message : String(e),
    );
    return false;
  }

  button.textContent = "Decrypting…";
  let plaintext: Uint8Array;
  try {
    plaintext = await decryptBundle(cek, sealed);
  } catch (e) {
    if (e instanceof DecryptError) {
      renderError(root, "Couldn't decrypt summary", e.message);
    } else {
      renderError(root, "Decryption failed", String(e));
    }
    return false;
  }

  // Sniff the decrypted plaintext. A raw PDF (`%PDF` magic) is a legacy
  // plain-PDF share — save it directly (unchanged). A `bh_share` JSON container
  // (encounter note / health summary / clinical summary / emergency card)
  // carries a PDF + optional FHIR bundle — render the richer dual-format view.
  const container = tryParseShareContainer(plaintext);
  if (container) {
    renderShareDocResult(root, container, props.metadata.expires_at);
    return true;
  }

  triggerBytesDownload(plaintext, "visit-summary.pdf", "application/pdf");
  return true;
}

/** Decoded self-describing share container (any doc kind). */
interface ShareDocContainer {
  kind: string;
  pdf: Uint8Array;
  fhir: unknown;
}

/** Container kinds that carry a PDF + optional FHIR data bundle. */
const KNOWN_DOC_KINDS = new Set([
  "encounter_note",
  "health_summary",
  "clinical_summary",
  "emergency_card",
]);

/**
 * Parse the self-describing share container
 * `{ "bh_share": 1, "kind": <doc-kind>, "pdf_b64": "...", "fhir_bundle": {...} }`.
 * Returns null when the plaintext is a raw PDF or an unrecognized shape, so the
 * legacy raw-PDF path stays unchanged and backward-compatible.
 */
function tryParseShareContainer(
  bytes: Uint8Array,
): ShareDocContainer | null {
  // Fast reject: a real PDF starts with "%PDF"; skip the JSON parse.
  if (bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
    return null;
  }
  let obj: Record<string, unknown>;
  try {
    obj = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return null;
  }
  const kind = obj?.["kind"];
  if (
    !obj ||
    obj["bh_share"] !== 1 ||
    typeof kind !== "string" ||
    !KNOWN_DOC_KINDS.has(kind)
  ) {
    return null;
  }
  const pdfB64 = obj["pdf_b64"];
  if (typeof pdfB64 !== "string") {
    return null;
  }
  const bin = atob(pdfB64);
  const pdf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    pdf[i] = bin.charCodeAt(i);
  }
  return { kind, pdf, fhir: obj["fhir_bundle"] ?? null };
}

/** Per-kind display metadata for the shared-document result view. */
const DOC_META: Record<string, { title: string; blurb: string; file: string }> = {
  encounter_note: {
    title: "Encounter note",
    blurb: "A clinician encounter note has been shared with you via BlockHaven.",
    file: "encounter-note",
  },
  health_summary: {
    title: "Health summary — from devices",
    blurb:
      "A wearable health summary (heart, sleep, activity, vitals) has been shared with you via BlockHaven.",
    file: "health-summary",
  },
  clinical_summary: {
    title: "Clinical summary",
    blurb:
      "A clinical summary (conditions, medications, labs) has been shared with you via BlockHaven.",
    file: "clinical-summary",
  },
  emergency_card: {
    title: "Emergency health card",
    blurb:
      "An emergency health card (allergies, medications, conditions) has been shared with you via BlockHaven.",
    file: "emergency-card",
  },
};

/**
 * Shared-document result view: inline PDF preview (native, via blob URL) plus a
 * PDF download and, when present, a FHIR data-bundle (.json) import for record
 * systems. Titled per container kind.
 */
function renderShareDocResult(
  root: HTMLElement,
  container: ShareDocContainer,
  expiresAt?: string,
): void {
  const pdfBlobUrl = URL.createObjectURL(
    new Blob([container.pdf.slice().buffer], { type: "application/pdf" }),
  );

  // Emergency card: an EPHEMERAL, view-only card. No download, no FHIR export —
  // it's meant to be glanced at now and to expire. The link's TTL + revoke are
  // the "kill"; we surface the expiry and disable the download affordance
  // (sandbox without allow-downloads). Break-glass into more than the card is
  // ADR-Platform-0020, not wired here.
  if (container.kind === "emergency_card") {
    const expiresHuman = expiresAt ? new Date(expiresAt).toLocaleString() : null;
    // Watermark: a screen-fixed, tiled, diagonal overlay carrying the view
    // timestamp and a non-redistribution notice, rendered as an SVG data URI
    // so it tiles with CSS `background-repeat`. `pointer-events: none` keeps it
    // from blocking reading/scroll of the card underneath. Deterrence + traceability
    // for a screenshot; there is no logged-in viewer on this path, so no PII.
    const wmStamp = new Date().toLocaleString();
    const wmSvg =
      `<svg xmlns='http://www.w3.org/2000/svg' width='340' height='190'>` +
      `<text x='170' y='86' transform='rotate(-30 170 95)' text-anchor='middle' ` +
      `font-family='sans-serif' font-size='15' font-weight='700' fill='rgba(110,122,119,0.20)'>` +
      `EMERGENCY ACCESS · VIEW ONCE</text>` +
      `<text x='170' y='108' transform='rotate(-30 170 95)' text-anchor='middle' ` +
      `font-family='sans-serif' font-size='11' fill='rgba(110,122,119,0.18)'>` +
      `${wmStamp} · NOT FOR REDISTRIBUTION</text></svg>`;
    const wmUrl = `url("data:image/svg+xml,${encodeURIComponent(wmSvg)}")`;
    root.innerHTML = `
      <div class="card">
        <h1>Emergency card</h1>
        <p>
          A patient's emergency card was shared with you. It is view-only and
          ${expiresHuman ? `expires <strong>${expiresHuman}</strong>` : "expires shortly"}.
          It cannot be downloaded or saved, and this link opens only once.
        </p>
        <div style="position:relative;">
          <iframe
            class="pdf-frame"
            src="${pdfBlobUrl}#toolbar=0&navpanes=0"
            title="Emergency card"
            sandbox="allow-same-origin">
          </iframe>
          <div aria-hidden="true" style="position:absolute; inset:0; pointer-events:none; background-image:${wmUrl}; background-repeat:repeat;"></div>
        </div>
        <p class="muted">View-only · opens once · not downloadable · expires and can be revoked by the patient.</p>
      </div>
    `;
    return;
  }

  const meta = DOC_META[container.kind] ?? {
    title: "Shared health document",
    blurb: "A health document has been shared with you via BlockHaven.",
    file: "health-document",
  };
  const hasFhir = container.fhir != null;

  root.innerHTML = `
    <div class="card">
      <h1>${meta.title}</h1>
      <p>
        ${meta.blurb} Review it below${hasFhir ? "; import the data file into your record system" : ""}.
      </p>
      <iframe
        class="pdf-frame"
        src="${pdfBlobUrl}#toolbar=0&navpanes=0"
        title="${meta.title}"
        sandbox="allow-scripts allow-same-origin allow-downloads">
      </iframe>
      <div class="actions">
        <button id="dl-pdf" class="primary">Download PDF</button>
        ${hasFhir ? `<button id="dl-fhir" class="secondary">Import data (FHIR)</button>` : ""}
      </div>
    </div>
  `;

  root.querySelector<HTMLButtonElement>("#dl-pdf")?.addEventListener("click", () => {
    triggerBytesDownload(container.pdf, `${meta.file}.pdf`, "application/pdf");
  });
  if (hasFhir) {
    root.querySelector<HTMLButtonElement>("#dl-fhir")?.addEventListener("click", () => {
      const json = JSON.stringify(container.fhir, null, 2);
      triggerBytesDownload(
        new TextEncoder().encode(json),
        `${meta.file}.fhir.json`,
        "application/fhir+json",
      );
    });
  }
}

async function postAck(
  apiBaseUrl: string,
  token: string,
  consentAck: boolean,
): Promise<void> {
  const url = `${apiBaseUrl}/share/links/${encodeURIComponent(token)}/ack`;
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ consent_ack: consentAck }),
    credentials: "omit",
  });
  if (!resp.ok) {
    throw new Error(`ack failed: ${resp.status}`);
  }
}

/** Plaintext variant: browser-native save straight from the link URL. */
function triggerPlaintextDownload(apiBaseUrl: string, token: string): void {
  const url = `${apiBaseUrl}/share/links/${encodeURIComponent(token)}`;
  const a = document.createElement("a");
  a.href = url;
  a.download = "visit-summary.pdf";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Save decrypted bytes via an object URL (mirrors share-session.ts). */
function triggerBytesDownload(
  bytes: Uint8Array,
  filename: string,
  contentType: string,
): void {
  const buf = bytes.slice().buffer;
  const blob = new Blob([buf], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function renderError(root: HTMLElement, title: string, body: string): void {
  root.innerHTML = `
    <div class="card error">
      <h1>${escapeHtml(title)}</h1>
      <p>${escapeHtml(body)}</p>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
